import React, { useState } from "react";
import styled, { ThemeProvider, keyframes } from "styled-components";
import { lightTheme } from "./Themes";
import { AnimatePresence, motion } from "framer-motion";
import { Work } from "../data/WorkData";
import { ProjectGalleries } from "../data/ProjectGalleryData";
import Card from "../subComponents/Card";
import ProjectGallery from "../subComponents/ProjectGallery";
import BigTitlte from "../subComponents/BigTitlte";
import PixelSprite from '../pixel/PixelSprites'

const Box = styled.div`
  background: repeating-linear-gradient(
      135deg,
      ${(props) => props.theme.bubbles}22 0px,
      ${(props) => props.theme.bubbles}22 40px,
      transparent 40px,
      transparent 80px
    ),
    ${(props) => props.theme.body};
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  padding: 8rem 2rem 9rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: 600px) {
    padding: 11rem 1rem 4rem;
  }
`;

const Intro = styled(motion.header)`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1180px;
  margin: 0 auto 2.5rem;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 2rem;
  align-items: end;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 1.2rem;
  }
`

const Kicker = styled.p`
  font-family: ${(props) => props.theme.pixelFont};
  font-size: 0.6rem;
  color: ${(props) => props.theme.blossom};
  margin-bottom: 0.8rem;
`

const PageTitle = styled.h2`
  font-size: clamp(1.5rem, 4vw, 3rem);
  color: ${(props) => props.theme.black};
  margin-bottom: 0.8rem;
`

const IntroText = styled.p`
  max-width: 58ch;
  font-size: 1.05rem;
  line-height: 1.6;
  font-weight: 600;
`

const ProgressPanel = styled.div`
  min-width: 17rem;
  background: #fff;
  border: 3px solid ${(props) => props.theme.black};
  padding: 0.9rem 1rem;
  box-shadow: 5px 5px 0 ${(props) => props.theme.black};
`

const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: ${(props) => props.theme.pixelFont};
  font-size: 0.52rem;
  margin-bottom: 0.65rem;
`

const ProgressTrack = styled.div`
  height: 0.85rem;
  border: 2px solid ${(props) => props.theme.black};
  background: ${(props) => props.theme.body};

  &::after {
    content: '';
    display: block;
    width: 82%;
    height: 100%;
    background: repeating-linear-gradient(
      90deg,
      ${(props) => props.theme.blossom} 0 18px,
      ${(props) => props.theme.bubbles} 18px 36px,
      ${(props) => props.theme.buttercup} 36px 54px
    );
  }
`

const Gallery = styled(motion.ul)`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 2.5rem;
  max-width: 1180px;
  width: 100%;
  margin: 0 auto;

  & > :last-child:nth-child(odd) {
    grid-column: 1 / -1;
    width: calc(50% - 1.25rem);
    justify-self: center;
  }

  @media (max-width: 760px) {
    grid-template-columns: 1fr;

    & > :last-child:nth-child(odd) {
      grid-column: auto;
      width: 100%;
    }
  }
`;

// Framer-motion configuration
const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      duration: 0.5,
    },
  },
};

const bounceX = keyframes`
  0%, 100% { transform: translateX(0); }
  50% { transform: translateX(6px); }
`

const MoreLink = styled(motion.a)`
  margin-top: 3.5rem;
  background: none;
  border: none;
  border-bottom: 3px dashed ${(props) => props.theme.blossom};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0 0 0.5rem;
  font-family: ${(props) => props.theme.pixelFont};
  font-size: clamp(0.85rem, 1.2vw, 1.05rem);
  color: ${(props) => props.theme.black};
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: ${(props) => props.theme.blossom};
  }
`

const Arrow = styled.span`
  display: inline-block;
  animation: ${bounceX} 1.2s ease-in-out infinite;
`


const WorkPage = () => {
  const [activeGallery, setActiveGallery] = useState(null);

  return (
    <ThemeProvider theme={lightTheme}>
      <Box>
        <BigTitlte text="PROJECT" top="4%" left="2%" />
        <Intro
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
        >
          <div>
            <Kicker>MISSION SELECT</Kicker>
            <PageTitle>Choose a Level</PageTitle>
            <IntroText>
              Five builds across mobile, automation, games, sustainability, and the web.
              Pick a mission to see what I shipped and how it was made.
            </IntroText>
          </div>
          <ProgressPanel>
            <ProgressLabel><span>PORTFOLIO XP</span><span>05 LEVELS</span></ProgressLabel>
            <ProgressTrack />
          </ProgressPanel>
        </Intro>
        <Gallery variants={container} initial="hidden" animate="show">
          {Work.map((d) => (
            <Card
              key={d.id}
              data={d}
              gallery={ProjectGalleries[d.id]}
              onOpen={setActiveGallery}
            />
          ))}
        </Gallery>

        <MoreLink
          href="https://github.com/hermiaTT?tab=repositories"
          target="_blank"
          rel="noreferrer"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.5 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <PixelSprite name="sparkle" size={4} color="#FF3E8E" />
          Explore more on GitHub
          <Arrow>➜</Arrow>
        </MoreLink>
        <AnimatePresence>
          {activeGallery && (
            <ProjectGallery gallery={activeGallery} onClose={() => setActiveGallery(null)} />
          )}
        </AnimatePresence>
      </Box>
    </ThemeProvider>
  );
};
export default WorkPage;
